import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useRTC } from "@/context/RTCContext";
import { useAuth } from "@/context/AuthContext";
import TimezonePicker from "@/components/TimezonePicker";
import {
  detectBrowserTimezone, fmtTimeInTZ, ymdInTZ, localStrInTZToUTC,
  utcToLocalStrInTZ, offsetLabelForTZ,
} from "@/lib/timezones";
import {
  Plus, CaretLeft, CaretRight, Calendar as CalendarIcon, Clock, Users, Phone,
  X, Trash, PencilSimple, VideoCamera, MagicWand,
} from "@phosphor-icons/react";
import FindATimeModal from "@/components/meetings/FindATimeModal";
import EmptyState from "@/components/EmptyState";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const TZ_STORAGE_KEY = "hbf_meetings_tz";
const LEGACY_TZ_STORAGE_KEYS = ["vocalis_meetings_tz"];

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59); }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtDateInTZ(iso, tz) {
  try {
    return new Intl.DateTimeFormat([], {
      timeZone: tz, weekday: "short", month: "short", day: "numeric",
    }).format(new Date(iso));
  } catch { return ""; }
}

/** Trigger a browser download of the meeting's ICS, hinting the recipient's TZ. */
async function downloadMeetingICS(meetingId, tz, title) {
  try {
    const resp = await api.get(`/meetings/${meetingId}/ics`, { params: { tz }, responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/calendar" }));
    const a = document.createElement("a");
    a.href = url;
    const safe = (title || "meeting").replace(/[^a-z0-9]+/gi, "-").slice(0, 60).replace(/-+$/g, "");
    a.download = `${safe || "meeting"}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    toast.success(`Calendar invite downloaded · times in ${tz}`);
  } catch (e) {
    toast.error("Could not download calendar invite");
  }
}

/** Google Calendar "add event" deep-link, pre-filled with the meeting + viewer TZ. */
function googleCalendarUrl(m, tz) {
  const fmt = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
  };
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: m.title || "Meeting",
    dates: `${fmt(m.start_at)}/${fmt(m.end_at)}`,
    details: m.description || "",
    ctz: tz || "UTC",
    location: `${window.location.origin}/app/meetings`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function Meetings() {
  const { user } = useAuth();
  const { joinRoom } = useRTC();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [meetings, setMeetings] = useState([]);
  const [members, setMembers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showQuickCall, setShowQuickCall] = useState(false);
  const [tz, setTz] = useState(() => {
    try {
      // Priority: explicit localStorage override > saved profile TZ > browser TZ.
      // Read active key first; migrate from legacy Vocalis-era keys if present.
      let v = localStorage.getItem(TZ_STORAGE_KEY);
      if (!v) {
        for (const legacy of LEGACY_TZ_STORAGE_KEYS) {
          const lv = localStorage.getItem(legacy);
          if (lv) { v = lv; localStorage.setItem(TZ_STORAGE_KEY, lv); localStorage.removeItem(legacy); break; }
        }
      }
      return v || user?.timezone || detectBrowserTimezone();
    } catch { return user?.timezone || detectBrowserTimezone(); }
  });
  useEffect(() => { try { localStorage.setItem(TZ_STORAGE_KEY, tz); } catch { /* noop */ } }, [tz]);
  // If the user's saved profile TZ arrives after mount (auth refresh), use it once
  // unless the user explicitly picked something else this session.
  useEffect(() => {
    if (user?.timezone && !localStorage.getItem(TZ_STORAGE_KEY + "_explicit") && tz !== user.timezone) {
      setTz(user.timezone);
    }
  }, [user?.timezone]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  const loadMeetings = useCallback(async () => {
    try {
      const { data } = await api.get("/meetings", {
        params: {
          date_from: monthStart.toISOString(),
          date_to: monthEnd.toISOString(),
        },
      });
      setMeetings(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load meetings");
    }
  }, [monthStart.getTime(), monthEnd.getTime()]);

  const loadMembers = useCallback(async () => {
    try {
      const { data } = await api.get("/internal-call/users");
      setMembers(data);
    } catch (err) {
      console.warn("Failed to load internal-call users (non-fatal):", err?.message || err);
    }
  }, []);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Auto-join a group call room when arriving from a bell notification. The
  // `incoming_call` persistent notification links to `/app/meetings?room=<id>`
  // — clicking should immediately drop us into the call, not just land on
  // the meetings calendar and force a second click.
  const autoJoinedRef = useRef(false);
  useEffect(() => {
    if (autoJoinedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const roomId = params.get("room");
    if (!roomId) return;
    autoJoinedRef.current = true;
    (async () => {
      try {
        await joinRoom({ room_id: roomId, title: "Group call" });
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 410 || (detail && String(detail).toLowerCase().includes("ended"))) {
          toast.error("Meeting has been ended", {
            description: "This call has already wrapped up. Ask the organizer to start a new one.",
            duration: 6000,
          });
        } else {
          toast.error("Could not join the call: " + (detail || e?.message || "unknown"));
        }
        // Clear the ?room= from the URL so a page refresh doesn't retry.
        try { window.history.replaceState({}, "", "/app/meetings"); } catch (_) { /* noop */ }
      }
    })();
  }, [joinRoom]);

  // Build calendar grid (6 weeks × 7 days)
  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const startWeekday = first.getDay();
    const days = [];
    const base = new Date(first);
    base.setDate(base.getDate() - startWeekday);
    for (let i = 0; i < 42; i++) {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      days.push(d);
    }
    return days;
  }, [cursor]);

  const meetingsByDay = useMemo(() => {
    const map = {};
    for (const m of meetings) {
      const k = ymdInTZ(m.start_at, tz);
      (map[k] = map[k] || []).push(m);
    }
    return map;
  }, [meetings, tz]);

  const upcoming = useMemo(() => {
    const now = Date.now();
    return [...meetings].filter((m) => new Date(m.end_at).getTime() >= now).slice(0, 6);
  }, [meetings]);

  // Live room status per upcoming meeting — powers the Join button visibility.
  // Backend `/rtc/room-status/{room_id}` returns { live, ended, ringing,
  // participant_count }. We poll every 8s while the meetings page is open so
  // an ended call flips its Join button to "Call ended" in near-real-time.
  const [roomStatuses, setRoomStatuses] = useState({});   // room_id -> status
  useEffect(() => {
    const rooms = upcoming.map((m) => m.room_name).filter(Boolean);
    if (rooms.length === 0) { setRoomStatuses({}); return undefined; }
    let cancelled = false;
    const poll = async () => {
      const entries = await Promise.all(rooms.map(async (rid) => {
        try {
          const { data } = await api.get(`/rtc/room-status/${encodeURIComponent(rid)}`);
          return [rid, data];
        } catch (_) {
          return [rid, null];
        }
      }));
      if (cancelled) return;
      setRoomStatuses(Object.fromEntries(entries));
    };
    poll();
    const iv = setInterval(poll, 8000);
    // iter130: authoritative flip when the server closes a room — no
    // need to wait for the 8 s poll cycle to remove the Join button.
    const onClosed = (e) => {
      const rid = e?.detail?.room_id;
      if (!rid || !rooms.includes(rid)) return;
      setRoomStatuses((cur) => ({
        ...cur,
        [rid]: { room_id: rid, live: false, ended: true, ringing: false, participant_count: 0 },
      }));
    };
    window.addEventListener("hbf:room-closed", onClosed);
    window.addEventListener("hbf:room-ended",  onClosed);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("hbf:room-closed", onClosed);
      window.removeEventListener("hbf:room-ended",  onClosed);
    };
  }, [upcoming.map((m) => m.room_name).join("|")]);

  const joinMeeting = async (m) => {
    try {
      const { data } = await api.post(`/meetings/${m.id}/join`);
      const res = await joinRoom({
        room_id: data.room_id,
        title: data.title || m.title,
        invitee_ids: data.invitee_ids || [],
      });
      if (res.ok) {
        toast.success(`Joined "${m.title}" — ringing ${data.invitee_ids?.length || 0} participant(s)`);
      }
      setDetail(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to join meeting");
    }
  };

  const cancelMeeting = async (m) => {
    if (!window.confirm(`Cancel meeting "${m.title}"?`)) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Meeting cancelled");
      setDetail(null);
      loadMeetings();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to cancel");
    }
  };

  const isOrganizer = (m) => m?.organizer_id === user?.id;
  const canManage = (m) => isOrganizer(m) || user?.role === "admin" || user?.role === "owner";

  return (
    <div className="hbf-hx" data-testid="meetings-page">
      <header className="hx-hero hx-up">
        <div>
          <div className="hx-hero__eyebrow"><CalendarIcon size={16} weight="duotone" color="#2563EB" /> Group calls &amp; schedule</div>
          <h1 className="hx-hero__title">Plan, meet, <em>flow</em></h1>
          <p className="hx-hero__sub">Schedule and join internal team meetings. All times shown in {tz} ({offsetLabelForTZ(new Date(), tz)}).</p>
        </div>
        <div className="hx-hero__cta" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <div className="hidden md:block min-w-[260px]">
            <TimezonePicker value={tz} onChange={setTz} testid="meetings-tz" />
          </div>
          <button
            onClick={() => setShowQuickCall(true)}
            className="hx-btn"
            data-testid="quick-internal-call-btn"
          >
            <Phone size={16} weight="fill" /> Quick group call
          </button>
          <button
            onClick={() => { setEditing(null); setShowCreate(true); }}
            className="hx-btn hx-btn--primary"
            data-testid="new-meeting-btn"
          >
            <Plus size={16} weight="bold" /> New meeting
          </button>
        </div>
      </header>

      <div className="md:hidden mt-3" data-testid="meetings-tz-mobile">
        <TimezonePicker value={tz} onChange={setTz} testid="meetings-tz" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mt-6">
        {/* Calendar grid */}
        <div className="hx-card p-5" data-testid="meetings-calendar">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
              className="hx-btn hx-btn--ghost hx-btn--sm"
              data-testid="cal-prev"
              aria-label="Previous month"
            ><CaretLeft size={16} /></button>
            <div className="font-display font-extrabold text-[22px] tracking-[-0.02em]" data-testid="cal-title">
              {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCursor(startOfMonth(new Date()))}
                className="hx-btn hx-btn--sm"
                data-testid="cal-today"
              >Today</button>
              <button
                onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                className="hx-btn hx-btn--ghost hx-btn--sm"
                data-testid="cal-next"
                aria-label="Next month"
              ><CaretRight size={16} /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px bg-[var(--hx-line)] border border-[var(--hx-line)] rounded-2xl overflow-hidden">
            {WEEKDAYS.map((w) => (
              <div key={w} className="bg-[var(--bg-paper)] py-2 text-center font-mono text-[10px] tracking-widest text-[var(--text-muted)]">{w}</div>
            ))}
            {grid.map((d, i) => {
              const inMonth = d.getMonth() === cursor.getMonth();
              const today = sameDay(d, new Date());
              const pad = (n) => String(n).padStart(2, "0");
              const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
              const items = meetingsByDay[k] || [];
              return (
                <div
                  key={k}
                  className={`bg-white min-h-[100px] p-2 transition-colors hover:bg-[#F8FAFF] ${inMonth ? "" : "opacity-40"} ${today ? "ring-2 ring-[var(--hx-blue)] ring-inset bg-[var(--hx-blue-soft)]/40" : ""}`}
                  data-testid={`cal-day-${d.toISOString().slice(0,10)}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[12px] font-bold ${today ? "text-[var(--hx-blue)]" : ""}`}>{d.getDate()}</span>
                    {items.length > 0 && (
                      <span className="text-[9px] font-bold text-[var(--hx-ink-3)] bg-[var(--hx-bg)] rounded-full px-1.5 py-0.5">{items.length}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {items.slice(0, 3).map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setDetail(m)}
                        className="text-left px-1.5 py-1 bg-[var(--hx-blue-soft)] text-[#1D4ED8] rounded-md text-[10px] font-bold truncate hover:bg-[var(--hx-blue)] hover:text-white transition-colors"
                        data-testid={`cal-event-${m.id}`}
                        title={`${m.title} · ${fmtTimeInTZ(m.start_at, tz)}`}
                      >
                        {fmtTimeInTZ(m.start_at, tz)} {m.title}
                      </button>
                    ))}
                    {items.length > 3 && (
                      <span className="text-[10px] text-[var(--hx-ink-3)] font-semibold">+{items.length - 3} more</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming sidebar */}
        <div className="hx-card p-5 self-start" data-testid="meetings-upcoming">
          <div className="text-[13px] font-extrabold tracking-[-0.01em] mb-3">Upcoming</div>
          {upcoming.length === 0 && (
            <EmptyState
              icon="meetings"
              title="No meetings scheduled"
              description="Set up a peer-to-peer team meeting — no Twilio cost, just secure WebRTC."
              compact
              testId="empty-state-meetings"
            />
          )}
          <div className="space-y-3">
            {upcoming.map((m) => {
              const status = roomStatuses[m.room_name];
              // Live means at least one participant is in the room right now.
              // Ended means the room was previously live and is now closed.
              // Scheduled means we're within/before start_at with no live room.
              const isLive    = !!status?.live;
              const isEnded   = !!status?.ended;
              const now       = Date.now();
              const started   = new Date(m.start_at).getTime() <= now;
              const canJoin   = !isEnded;  // Always allow joining unless server marked ended.
              return (
                <div key={m.id} className="border border-[var(--hx-line)] rounded-2xl p-3.5 transition-all hover:shadow-lg hover:-translate-y-0.5 bg-white" data-testid={`upcoming-${m.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <div className="font-bold text-[13px] truncate">{m.title}</div>
                    <div className="flex items-center gap-1.5">
                      {isLive && (
                        <span data-testid={`meeting-live-${m.id}`} className="text-[9px] font-extrabold tracking-[.1em] text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE · {status.participant_count}
                        </span>
                      )}
                      {isEnded && (
                        <span data-testid={`meeting-ended-${m.id}`} className="text-[9px] font-extrabold tracking-[.1em] text-rose-700 bg-rose-100 rounded-full px-2 py-0.5">CALL ENDED</span>
                      )}
                      {isOrganizer(m) && (
                        <span className="text-[9px] font-extrabold tracking-[.1em] text-[var(--hx-blue)] bg-[var(--hx-blue-soft)] rounded-full px-2 py-0.5">HOST</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-[var(--hx-ink-3)] font-semibold mb-1">
                    <CalendarIcon size={12} />
                    <span>{fmtDateInTZ(m.start_at, tz)}</span>
                    <Clock size={12} />
                    <span>{fmtTimeInTZ(m.start_at, tz)}–{fmtTimeInTZ(m.end_at, tz)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mb-2.5">
                    <Users size={12} className="text-[var(--hx-ink-3)]" />
                    <span className="text-[11px] text-[var(--hx-ink-3)] font-semibold">{(m.participants?.length || 0) + 1} people</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => canJoin && joinMeeting(m)}
                      disabled={!canJoin}
                      title={isEnded ? "This call has already ended" : (started ? "Join now" : "Join the meeting")}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 transition-colors
                        ${!canJoin
                          ? "bg-[var(--hx-line)] text-[var(--hx-ink-3)] cursor-not-allowed"
                          : isLive
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                            : "bg-[var(--hx-blue)] hover:bg-[var(--hx-blue-deep)] text-white"}`}
                      data-testid={`join-meeting-${m.id}`}
                    >
                      <VideoCamera size={12} weight="fill" />
                      {isEnded ? "Ended" : isLive ? "Join live" : "Join"}
                    </button>
                    <button
                      onClick={() => setDetail(m)}
                      className="px-2.5 py-1.5 border border-[var(--hx-line)] rounded-lg text-[11px] font-bold hover:bg-[var(--hx-line-soft)] transition-colors"
                      data-testid={`view-meeting-${m.id}`}
                    >Details</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {showCreate && (
        <MeetingFormModal
          tz={tz}
          onClose={() => { setShowCreate(false); setEditing(null); }}
          onSaved={() => { setShowCreate(false); setEditing(null); loadMeetings(); }}
          members={members}
          editing={editing}
        />
      )}

      {detail && (
        <MeetingDetailModal
          tz={tz}
          meeting={detail}
          onClose={() => setDetail(null)}
          onJoin={() => joinMeeting(detail)}
          onEdit={() => { setEditing(detail); setDetail(null); setShowCreate(true); }}
          onCancel={() => cancelMeeting(detail)}
          canManage={canManage(detail)}
        />
      )}

      {showQuickCall && (
        <QuickGroupCallModal
          members={members}
          onClose={() => setShowQuickCall(false)}
          onStarted={() => setShowQuickCall(false)}
        />
      )}
    </div>
  );
}

function MeetingFormModal({ onClose, onSaved, members, editing, tz }) {
  const { user } = useAuth();
  const now = new Date();
  const in1h = new Date(now.getTime() + 60 * 60 * 1000);
  const in1h30 = new Date(now.getTime() + 90 * 60 * 1000);
  const [title, setTitle] = useState(editing?.title || "");
  const [description, setDescription] = useState(editing?.description || "");
  // datetime-local strings are interpreted as wall-clock time in `tz` and converted on save.
  const [startAt, setStartAt] = useState(editing
    ? utcToLocalStrInTZ(editing.start_at, tz)
    : utcToLocalStrInTZ(in1h.toISOString(), tz));
  const [endAt, setEndAt] = useState(editing
    ? utcToLocalStrInTZ(editing.end_at, tz)
    : utcToLocalStrInTZ(in1h30.toISOString(), tz));
  const [selected, setSelected] = useState(() => new Set(editing?.participant_ids || []));
  const [saving, setSaving] = useState(false);
  const [showFindTime, setShowFindTime] = useState(false);

  // Minutes between current startAt/endAt — used as the scanner's duration default.
  const duration = useMemo(() => {
    const a = localStrInTZToUTC(startAt, tz);
    const b = localStrInTZToUTC(endAt, tz);
    if (!a || !b) return 30;
    const m = Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60000);
    return Math.max(15, Math.min(480, m));
  }, [startAt, endAt, tz]);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    if (!startAt || !endAt) { toast.error("Start and end time required"); return; }
    setSaving(true);
    try {
      const startUtc = localStrInTZToUTC(startAt, tz);
      const endUtc = localStrInTZToUTC(endAt, tz);
      if (new Date(endUtc) <= new Date(startUtc)) {
        toast.error("End time must be after start");
        setSaving(false);
        return;
      }
      const payload = {
        title: title.trim(),
        description: description.trim(),
        start_at: startUtc,
        end_at: endUtc,
        participant_ids: Array.from(selected),
      };
      if (editing) {
        await api.patch(`/meetings/${editing.id}`, payload);
        toast.success("Meeting updated");
      } else {
        await api.post("/meetings", payload);
        toast.success("Meeting scheduled");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="hx-scrim" onClick={onClose}>
      <div className="hx-modal max-h-[90vh] overflow-y-auto" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="meeting-form-modal">
        <div className="hx-modal__head">
          <div className="hx-modal__title">{editing ? "Edit meeting" : "New meeting"}</div>
          <button onClick={onClose} className="hx-btn hx-btn--ghost hx-btn--sm" data-testid="meeting-form-close"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-1">TITLE</label>
            <input className="input" placeholder="Weekly standup" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="meeting-title-input" />
          </div>
          <div>
            <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-1">DESCRIPTION (OPTIONAL)</label>
            <textarea className="input min-h-[80px]" placeholder="Agenda, links, notes…" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="meeting-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex items-center justify-between">
              <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)]">SCHEDULE</label>
              <button
                type="button"
                onClick={() => setShowFindTime(true)}
                disabled={selected.size === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[var(--accent)] text-[var(--accent)] text-[11px] font-semibold hover:bg-[var(--accent)] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={selected.size === 0 ? "Pick at least one participant first" : "Scan participants' availability"}
                data-testid="meeting-find-a-time-btn"
              >
                <MagicWand size={11} weight="fill" /> Find a time
              </button>
            </div>
            <div>
              <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-1">STARTS</label>
              <input type="datetime-local" className="input" value={startAt} onChange={(e) => setStartAt(e.target.value)} data-testid="meeting-start-input" />
            </div>
            <div>
              <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-1">ENDS</label>
              <input type="datetime-local" className="input" value={endAt} onChange={(e) => setEndAt(e.target.value)} data-testid="meeting-end-input" />
            </div>
            <div className="col-span-2 text-[11px] text-[var(--text-muted)] font-mono flex items-center gap-1.5" data-testid="meeting-form-tz-hint">
              <CalendarIcon size={11} />
              Times are entered in <span className="font-semibold text-[var(--text-primary)]">{tz}</span> ({offsetLabelForTZ(new Date(), tz)}). Change the timezone in the page header.
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-1">
              PARTICIPANTS ({selected.size} selected)
            </label>
            <div className="border border-[var(--border)] max-h-[200px] overflow-y-auto">
              {members.length === 0 && (
                <div className="p-3 text-[12px] text-[var(--text-muted)]">No teammates available. Invite team members from the Team page.</div>
              )}
              {members.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 border-b border-[var(--border)] hover:bg-[var(--bg)] cursor-pointer" data-testid={`member-pick-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(u.id)}
                    onChange={() => toggle(u.id)}
                    data-testid={`member-checkbox-${u.id}`}
                  />
                  <img src={u.avatar_url} alt="" className="w-6 h-6 border border-[var(--border)]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold truncate">{u.name}</div>
                    <div className="text-[10px] font-mono text-[var(--text-muted)] tracking-widest">{u.role?.toUpperCase()}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="meeting-form-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="meeting-form-save">
            {saving ? "Saving…" : (editing ? "Update" : "Schedule")}
          </button>
        </div>
      </div>

      <FindATimeModal
        open={showFindTime}
        onClose={() => setShowFindTime(false)}
        participantIds={Array.from(new Set([user?.id, ...selected].filter(Boolean)))}
        viewerTz={tz}
        duration={duration}
        onPick={({ startUtc, endUtc }) => {
          setStartAt(utcToLocalStrInTZ(startUtc, tz));
          setEndAt(utcToLocalStrInTZ(endUtc, tz));
          setShowFindTime(false);
          toast.success("Slot picked — review and click Schedule");
        }}
      />
    </div>
  );
}

function MeetingDetailModal({ meeting, onClose, onJoin, onEdit, onCancel, canManage, tz }) {
  const m = meeting;
  return (
    <div className="hx-scrim" onClick={onClose}>
      <div className="hx-modal max-h-[90vh] overflow-y-auto" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()} data-testid="meeting-detail-modal">
        <div className="hx-modal__head">
          <div className="hx-modal__title truncate pr-4">{m.title}</div>
          <button onClick={onClose} className="hx-btn hx-btn--ghost hx-btn--sm"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[13px]">
            <CalendarIcon size={14} className="text-[var(--text-muted)]" />
            <span>{fmtDateInTZ(m.start_at, tz)} · {fmtTimeInTZ(m.start_at, tz)}–{fmtTimeInTZ(m.end_at, tz)}</span>
            <span className="font-mono text-[10px] text-[var(--text-muted)]">({tz} · {offsetLabelForTZ(new Date(m.start_at), tz)})</span>
          </div>
          {m.description && (
            <div className="text-[13px] whitespace-pre-wrap text-[var(--text-secondary)]">{m.description}</div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => downloadMeetingICS(m.id, tz, m.title)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)] text-[12px] font-semibold transition-colors"
              data-testid="meeting-add-to-calendar"
              title="Download .ics file for Apple Calendar, Thunderbird, Outlook…"
            >
              <CalendarIcon size={12} weight="fill" /> Add to calendar (.ics)
            </button>
            <a
              href={googleCalendarUrl(m, tz)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)] text-[12px] font-semibold transition-colors"
              data-testid="meeting-add-to-gcal"
              title="Open in Google Calendar (web)"
            >
              <CalendarIcon size={12} /> Google Calendar
            </a>
          </div>
          <div>
            <div className="text-[11px] font-mono tracking-widest text-[var(--text-muted)] mb-2">PARTICIPANTS</div>
            <div className="flex flex-col gap-1.5">
              {m.organizer && (
                <div className="flex items-center gap-2 text-[13px]" data-testid="organizer-row">
                  <img src={m.organizer.avatar_url} alt="" className="w-7 h-7 border border-[var(--border)]" />
                  <span className="font-semibold">{m.organizer.name}</span>
                  <span className="text-[9px] font-mono tracking-widest text-[var(--accent)]">HOST</span>
                </div>
              )}
              {(m.participants || []).map((u) => (
                <div key={u.id} className="flex items-center gap-2 text-[13px]">
                  <img src={u.avatar_url} alt="" className="w-7 h-7 border border-[var(--border)]" />
                  <span>{u.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-[var(--border)] flex justify-between gap-2">
          <div className="flex gap-2">
            {canManage && (
              <>
                <button onClick={onEdit} className="px-3 py-2 border border-[var(--border)] text-[var(--text-primary)] text-[12px] font-semibold flex items-center gap-1.5 hover:bg-[var(--border)] transition-colors" data-testid="meeting-edit-btn">
                  <PencilSimple size={12} /> Edit
                </button>
                <button onClick={onCancel} className="px-3 py-2 border border-red-500/50 text-red-400 text-[12px] font-semibold flex items-center gap-1.5 hover:bg-red-500/10 transition-colors" data-testid="meeting-cancel-btn">
                  <Trash size={12} /> Cancel
                </button>
              </>
            )}
          </div>
          <button onClick={onJoin} className="btn-primary flex items-center gap-2" data-testid="meeting-join-btn">
            <VideoCamera size={14} weight="fill" /> Join now
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickGroupCallModal({ members, onClose, onStarted }) {
  const { joinRoom } = useRTC();
  const [selected, setSelected] = useState(new Set());
  const [starting, setStarting] = useState(false);

  const toggle = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const start = async () => {
    if (selected.size === 0) { toast.error("Pick at least one teammate"); return; }
    setStarting(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/internal-call/start", { participant_ids: ids });
      const res = await joinRoom({
        room_id: data.room_id,
        title: data.title || `Group call (${ids.length})`,
        invitee_ids: data.invitee_ids || [],
      });
      if (res.ok) {
        toast.success(`Ringing ${data.invitee_ids?.length || 0} teammate(s)…`);
      }
      onStarted();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Call failed");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="hx-scrim" onClick={onClose}>
      <div className="hx-modal max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quick-call-modal">
        <div className="hx-modal__head">
          <div>
            <div className="hx-modal__title">Quick group call</div>
            <div className="text-[12px] text-[var(--hx-ink-3)] mt-0.5 font-medium">Ring teammates instantly — no scheduling.</div>
          </div>
          <button onClick={onClose} className="hx-btn hx-btn--ghost hx-btn--sm" data-testid="quick-call-close"><X size={16} /></button>
        </div>
        <div className="p-4">
          <div className="border border-[var(--border)] max-h-[300px] overflow-y-auto">
            {members.length === 0 && (
              <div className="p-3 text-[12px] text-[var(--text-muted)]">No teammates available.</div>
            )}
            {members.map((u) => (
              <label key={u.id} className="flex items-center gap-2 p-2 border-b border-[var(--border)] hover:bg-[var(--bg)] cursor-pointer" data-testid={`quick-call-member-${u.id}`}>
                <input
                  type="checkbox"
                  checked={selected.has(u.id)}
                  onChange={() => toggle(u.id)}
                  data-testid={`quick-call-checkbox-${u.id}`}
                />
                <img src={u.avatar_url} alt="" className="w-7 h-7 border border-[var(--border)]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold truncate">{u.name}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)] tracking-widest">{u.role?.toUpperCase()}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
        <div className="p-4 border-t border-[var(--border)] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="quick-call-cancel">Cancel</button>
          <button onClick={start} disabled={starting} className="btn-primary flex items-center gap-2" data-testid="quick-call-start">
            <Phone size={14} weight="fill" />
            {starting ? "Starting…" : `Call ${selected.size || ""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

